import { buildClient } from '@lunacast/lib-network';
import { createAccessTokenStore } from './accessTokenStore.js';
import { createDeviceKeyStore } from './deviceKey.js';
import { authRefreshLayer } from './layers/authRefreshLayer.js';
import { dpopHeaderLayer } from './layers/dpopHeaderLayer.js';
import { baseUrlLayer } from './layers/baseUrlLayer.js';
function parseRefreshPayload(body) {
    if (typeof body !== 'object' || body === null) {
        return null;
    }
    const payload = body.payload;
    if (typeof payload !== 'object' || payload === null) {
        return null;
    }
    const data = payload;
    if (typeof data.accessToken !== 'string' || typeof data.accessTokenExpiresAt !== 'string') {
        return null;
    }
    return {
        accessToken: data.accessToken,
        accessTokenExpiresAt: data.accessTokenExpiresAt,
        familyId: typeof data.familyId === 'string' ? data.familyId : undefined,
    };
}
/**
 * 액세스 토큰 저장·선제 갱신·DPoP 서명·401 재시도가 붙은 인증 클라이언트를 만든다.
 */
export function createAuthClient(options) {
    const accessTokenStore = createAccessTokenStore();
    const deviceKeyStore = createDeviceKeyStore(options.storageNamespace);
    const refreshFetcher = buildClient(dpopHeaderLayer(deviceKeyStore), baseUrlLayer(options.baseUrl));
    let refreshPromise = null;
    async function refreshSession() {
        // 동시 갱신 요청은 하나의 Promise로 합친다. RT 재사용·경쟁을 막는다.
        if (refreshPromise) {
            return refreshPromise;
        }
        refreshPromise = (async () => {
            try {
                const res = await refreshFetcher('/auth/refresh', {
                    method: 'POST',
                    credentials: 'include',
                    // body·Content-Type 없음 — application/json 만 보내면 Express가 {} 로 파싱해
                    // 계약(z.null().optional())과 어긋난다.
                });
                if (!res.ok) {
                    accessTokenStore.clearAccessToken();
                    return false;
                }
                const data = parseRefreshPayload(await res.json());
                if (!data) {
                    accessTokenStore.clearAccessToken();
                    return false;
                }
                accessTokenStore.setAccessToken(data.accessToken, data.accessTokenExpiresAt, data.familyId);
                return true;
            }
            catch {
                accessTokenStore.clearAccessToken();
                return false;
            }
            finally {
                refreshPromise = null;
            }
        })();
        return refreshPromise;
    }
    accessTokenStore.setRefreshHandler(async () => {
        await refreshSession();
    });
    const authedFetch = buildClient(authRefreshLayer({
        accessTokenStore,
        refreshSession,
        onSessionExpired: options.onSessionExpired,
    }), dpopHeaderLayer(deviceKeyStore), baseUrlLayer(options.baseUrl));
    const publicFetch = buildClient(baseUrlLayer(options.baseUrl));
    return {
        fetch: authedFetch,
        fetchPublic: publicFetch,
        getAccessToken: accessTokenStore.getAccessToken,
        getFamilyId: accessTokenStore.getFamilyId,
        setAccessToken: accessTokenStore.setAccessToken,
        clearAccessToken: accessTokenStore.clearAccessToken,
        refreshSession,
        createDpopProof: (input) => deviceKeyStore.createDpopProof(input),
    };
}
//# sourceMappingURL=client.js.map